import React, { useEffect } from "react";
import axios from "axios";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import {
  useCreateReportMutation,
  useGeneratePresignedUrlMutation,
} from "../../../services/reportService";
import { useFetchCurrentUserQuery } from "../../../services/profileService";
import useAuth from "../../../hooks/useAuth";

import Firsttable from "../../report/component/Firsttable";
import DashboardCharts from "../../report/component/DashboardCharts";
import DataComp from "../../report/component/DataComp";
import WorkData from "../../report/component/WorkData";
import ReportHeader from "../../../components/Common/ReportHeader";
import ReportFooter from "../../../components/Common/ReportFooter";

import styles from "../ManagerView.module.css";
import logos from "../../../assets/images/rolereview_logo.png";

const SectionWrap = styled(Box)(() => ({
  padding: "50px 0",
}));

const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "1170px",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));

const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));

const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));

const HiddenReportGenerator = ({
  userId,
  action,
  existingPdfPath,
  onComplete,
}) => {
  const { users } = useAuth();
  const managerId = users?.manager_id;
  const role = users?.role;

  const [generatePresignedUrl] = useGeneratePresignedUrlMutation();
  const [createReport] = useCreateReportMutation();
  const { data: userData } = useFetchCurrentUserQuery(userId);

  useEffect(() => {
    // Wait for components to mount and fetch data before capturing
    const timeout = setTimeout(async () => {
      try {
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = 210;
        const pageHeight = 297;
        const margin = 10;
        const bottomMargin = 10;

        // Capture header
        const headerElement = document.getElementById("pdfHeader-hidden");
        headerElement.classList.add(styles.forceHF);
        const roleReviewTitle = headerElement.querySelector("#RolerevirTitle");
        const roleReviewContent = headerElement.querySelector("#RolerevirContent");

        const originalTitleFontSize = roleReviewTitle?.style.fontSize;
        const originalContentFontSize = roleReviewContent?.style.fontSize;
        if (roleReviewTitle) roleReviewTitle.style.fontSize = "55px";
        if (roleReviewContent) roleReviewContent.style.fontSize = "35px";

        const headerCanvas = await html2canvas(headerElement, {
          scale: 2,
          useCORS: true,
        });
        const headerImgData = headerCanvas.toDataURL("image/jpeg", 0.6);
        const headerImgHeight =
          (headerCanvas.height * pageWidth) / headerCanvas.width;
        
        if (roleReviewTitle) roleReviewTitle.style.fontSize = originalTitleFontSize;
        if (roleReviewContent) roleReviewContent.style.fontSize = originalContentFontSize;

        // Capture footer
        const footerElement = document.getElementById("pdfFooter-hidden");
        footerElement.classList.add(styles.forceHF);
        const footerTitle = footerElement.querySelector("#FooterTitle");

        const originalfooterTitleSize = footerTitle?.style.fontSize;
        if (footerTitle) {
          footerTitle.style.marginBottom = "55px";
          footerTitle.style.fontSize = "46px";
        }
        
        footerElement.querySelectorAll("a").forEach((a) => (a.style.display = "none"));
        footerElement
          .querySelectorAll("#forMoreInfo, #contactOrVisit")
          .forEach((span) => (span.style.display = "none"));

        const footerCanvas = await html2canvas(footerElement, {
          scale: 2,
          useCORS: true,
        });
        const footerImgData = footerCanvas.toDataURL("image/jpeg", 0.6);
        const footerImgHeight =
          (footerCanvas.height * pageWidth) / footerCanvas.width;
        
        if (footerTitle) {
          footerTitle.style.marginBottom = "";
          footerTitle.style.fontSize = originalfooterTitleSize;
        }
        footerElement.querySelectorAll("a").forEach((a) => (a.style.display = ""));
        footerElement
          .querySelectorAll("#forMoreInfo, #contactOrVisit")
          .forEach((span) => (span.style.display = ""));

        // Capture pages
        const captureAndAddToPDF = async (elementId) => {
          const addHeaderAndFooter = () => {
            pdf.addImage(headerImgData, "jpeg", 0, 0, pageWidth, headerImgHeight);
            const logoWidth = 22;
            const logoHeight = 22;
            pdf.addImage(logos, "PNG", 5, 5, logoWidth, logoHeight);
            pdf.addImage(
              footerImgData,
              "jpeg",
              0,
              pageHeight - footerImgHeight,
              pageWidth,
              footerImgHeight
            );
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(13);
            const yText = pageHeight - 6;
            pdf.text("For more information, contact", 13, yText);
            pdf.textWithLink("info@activatehcg.com", 75, yText, {
              url: "mailto:info@activatehcg.com",
            });
            pdf.text("or visit our website", 122, yText);
            pdf.textWithLink("ActivateHCG.com", 162, yText, {
              url: "https://www.activatehcg.com",
            });
          };

          const element = document.getElementById(elementId);
          element.classList.add(styles.desktop);
          if (window.innerWidth < 768) {
            element.style.width = "1170px";
            element.classList.add(styles.forceDesktop);
          }
          const contentCanvas = await html2canvas(element, {
            width: 1170,
            height: element.offsetHeight,
            scale: 2,
            useCORS: true,
          });

          const availableHeight =
            pageHeight - headerImgHeight - footerImgHeight - margin - bottomMargin;

          const canvasSlice = document.createElement("canvas");
          canvasSlice.width = contentCanvas.width;
          const pixelRatio = contentCanvas.width / pageWidth;
          const sliceHeightPx = availableHeight * pixelRatio;

          let yPositionPx = 0;
          let pageCount = 0;

          while (yPositionPx < contentCanvas.height) {
            const currentSliceHeight = Math.min(
              sliceHeightPx,
              contentCanvas.height - yPositionPx
            );
            canvasSlice.height = currentSliceHeight;

            const ctx = canvasSlice.getContext("2d");
            ctx.clearRect(0, 0, canvasSlice.width, canvasSlice.height);
            ctx.drawImage(
              contentCanvas,
              0,
              yPositionPx,
              contentCanvas.width,
              currentSliceHeight,
              0,
              0,
              canvasSlice.width,
              currentSliceHeight
            );

            const sliceData = canvasSlice.toDataURL("image/jpeg");
            const slicePdfHeight =
              (currentSliceHeight * pageWidth) / contentCanvas.width;

            if (pageCount === 0) {
              addHeaderAndFooter();
              pdf.addImage(
                sliceData,
                "jpeg",
                0,
                headerImgHeight + margin,
                pageWidth,
                slicePdfHeight
              );
            } else {
              pdf.addPage();
              addHeaderAndFooter();
              pdf.addImage(
                sliceData,
                "jpeg",
                0,
                headerImgHeight + margin,
                pageWidth,
                slicePdfHeight
              );
            }

            yPositionPx += currentSliceHeight;
            pageCount++;
          }
        };

        await captureAndAddToPDF("firstPage-hidden");
        pdf.addPage();
        await captureAndAddToPDF("secondPage-hidden");

        const pdfBlob = pdf.output("blob");

        // Determine filename to upload to Azure
        let fileName;
        if (existingPdfPath) {
          fileName = existingPdfPath; // Use existing, so we don't need a DB update
        } else {
          const today = new Date();
          const formattedDate = `${(today.getMonth() + 1)
            .toString()
            .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}-${today.getFullYear()}`;
          const employeeName = userData?.data?.name.replace(/\s+/g, "_") || "User";
          fileName = `RoleReview–${employeeName}-${formattedDate}.pdf`;
        }
        
        // Sometimes existingPdfPath might be full path, we just need basename for generation API
        const fileBasename = fileName.split("/").pop();

        // Upload to Azure
        const presignedUrlResponse = await generatePresignedUrl({
          file_name: fileBasename,
          file_type: "application/pdf",
        });

        const presignedUrl = presignedUrlResponse?.data?.data?.presigned_url;

        if (presignedUrl) {
          await axios.put(presignedUrl, pdfBlob, {
            headers: {
              "x-ms-blob-type": "BlockBlob",
              "Content-Type": "application/pdf",
            },
          });

          // Only create report DB record if it never existed
          if (!existingPdfPath) {
            const formData = new FormData();
            formData.append("user_id", userId);
            formData.append("manager_id", managerId ?? userId);
            formData.append("file", fileBasename);
            formData.append("role", role);
            await createReport(formData);
          }

          // Open or Download
          const downloadBlobUrl = window.URL.createObjectURL(pdfBlob);
          if (action === "view") {
            window.open(downloadBlobUrl, "_blank");
          } else {
            const downloadLink = document.createElement("a");
            downloadLink.href = downloadBlobUrl;
            downloadLink.setAttribute("download", fileBasename);
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            window.URL.revokeObjectURL(downloadBlobUrl);
          }
        }

        // Notify parent we are done
        if (onComplete) onComplete(true);

      } catch (error) {
        console.error("Error generating hidden PDF:", error);
        if (onComplete) onComplete(false);
      }
    }, 2500); // give time for charts to render

    return () => clearTimeout(timeout);
  }, [
    userId,
    managerId,
    role,
    action,
    existingPdfPath,
    generatePresignedUrl,
    createReport,
    userData,
    onComplete,
  ]);

  return (
    <Box
      sx={{
        position: "absolute",
        top: "-9999px",
        left: "-9999px",
        width: "1170px",
        opacity: 0,
        zIndex: -100,
        pointerEvents: "none",
      }}
    >
      <div id="pdfHeader-hidden">
        <ReportHeader />
      </div>
      <SectionWrap component="section" sx={{ position: "relative" }}>
        <ContainerWrap>
          <RowWrap>
            <Col12Wrap>
              <Box id="firstPage-hidden">
                <Firsttable userId={userId} />
                <DashboardCharts userId={userId} />
                <Box id="dataComp-hidden">
                  <DataComp userId={userId} />
                </Box>
              </Box>
              <Box id="secondPage-hidden" style={{ marginTop: "50px" }}>
                <WorkData userId={userId} />
              </Box>
            </Col12Wrap>
          </RowWrap>
        </ContainerWrap>
      </SectionWrap>
      <div id="pdfFooter-hidden">
        <ReportFooter />
      </div>
    </Box>
  );
};

export default HiddenReportGenerator;
